import {
    createContext,
    type FC,
    type PropsWithChildren,
    type RefObject,
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react';

type ProductDetailScrollContextValue = {
    venuesSectionRef: RefObject<HTMLDivElement | null>;
    scrollToVenues: () => void;
    reviewsSectionRef: RefObject<HTMLDivElement | null>;
    scrollToReviews: () => void;
};

const ProductDetailScrollContext = createContext<ProductDetailScrollContextValue | undefined>(
    undefined,
);

const ProductDetailScrollProvider: FC<PropsWithChildren> = ({ children }) => {
    const venuesSectionRef = useRef<HTMLDivElement>(null);
    const reviewsSectionRef = useRef<HTMLDivElement>(null);

    const scrollToVenues = useCallback((): void => {
        venuesSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const scrollToReviews = useCallback((): void => {
        reviewsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const value = useMemo(
        () => ({
            venuesSectionRef,
            scrollToVenues,
            reviewsSectionRef,
            scrollToReviews,
        }),
        [scrollToReviews, scrollToVenues],
    );

    return <ProductDetailScrollContext value={value}>{children}</ProductDetailScrollContext>;
};

function useProductDetailScroll(): ProductDetailScrollContextValue {
    const context = useContext(ProductDetailScrollContext);

    if (!context) {
        return {
            venuesSectionRef: { current: null },
            scrollToVenues: () => {},
            reviewsSectionRef: { current: null },
            scrollToReviews: () => {},
        };
    }

    return context;
}

export { ProductDetailScrollProvider, useProductDetailScroll };
export type { ProductDetailScrollContextValue };
